import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTheme } from '../theme/context/ThemeContext';
import { 
  FolderIcon, 
  DocumentIcon,
  ArrowUpTrayIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  DocumentTextIcon,
  PhotoIcon,
  FilmIcon,
  MusicalNoteIcon
} from '@heroicons/react/24/outline';

/**
 * ClientView Component (Client Dashboard)
 * 
 * File store for client documents
 */
const ClientView = ({ user, onChatSelect }) => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const fileInputRef = useRef(null);
  
  const [client, setClient] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch client data and files
  const fetchClientData = useCallback(async () => {
    if (!clientId) return;

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      
      const [clientResponse, filesResponse] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_API_URL}/clients/${clientId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        axios.get(
          `${process.env.REACT_APP_API_URL}/clients/${clientId}/files`,
          { headers: { Authorization: `Bearer ${token}` } }
        )
      ]);
      
      setClient(clientResponse.data.client);
      setFiles(filesResponse.data.files || []);
    } catch (err) {
      console.error('Error fetching client data:', err);
      setError(err.response?.data?.message || 'Failed to load client');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchClientData();
  }, [fetchClientData]);

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/clients/${clientId}/files`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Add new file to list
      setFiles(prev => [response.data.file, ...prev]);
    } catch (err) {
      console.error('Error uploading file:', err);
      alert(err.response?.data?.message || 'Failed to upload file');
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle file delete
  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/clients/${clientId}/files/${fileId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Remove from list
      setFiles(prev => prev.filter(f => f._id !== fileId));
    } catch (err) {
      console.error('Error deleting file:', err);
      alert(err.response?.data?.message || 'Failed to delete file');
    }
  };

  // Get file icon based on mime type
  const getFileIcon = (mimeType) => {
    if (mimeType.startsWith('image/')) return PhotoIcon;
    if (mimeType.startsWith('video/')) return FilmIcon;
    if (mimeType.startsWith('audio/')) return MusicalNoteIcon;
    if (mimeType.includes('pdf') || mimeType.includes('document') || mimeType.includes('text')) return DocumentTextIcon;
    return DocumentIcon;
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Truncate filename from the middle, keeping the extension visible
  const truncateMiddle = (str, maxLength = 28) => {
    if (!str || str.length <= maxLength) return str;
    
    // Find the extension
    const lastDot = str.lastIndexOf('.');
    const ext = lastDot > 0 ? str.slice(lastDot) : '';
    const name = lastDot > 0 ? str.slice(0, lastDot) : str;
    
    // Calculate how much of the name we can show
    const availableLength = maxLength - ext.length - 3; // 3 for "..."
    if (availableLength <= 4) return str.slice(0, maxLength - 3) + '...';
    
    const frontLength = Math.ceil(availableLength / 2);
    const backLength = Math.floor(availableLength / 2);
    
    return name.slice(0, frontLength) + '...' + name.slice(-backLength) + ext;
  };

  // Filter files by search
  const filteredFiles = files.filter(file => 
    file.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Show loading state
  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center h-full bg-white dark:bg-[#161616]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-500 dark:text-gray-400" style={{ fontFamily: 'var(--chat-font-family)' }}>
            Loading...
          </p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center h-full bg-white dark:bg-[#161616]">
        <div className="text-center max-w-md px-6">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-2" style={{ fontFamily: 'var(--chat-font-family)' }}>
            Unable to Load Client
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-4" style={{ fontFamily: 'var(--chat-font-family)' }}>
            {error}
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            style={{ fontFamily: 'var(--chat-font-family)' }}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-full bg-white dark:bg-[#111111] overflow-hidden">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={handleFileUpload}
      />

      {/* Content wrapper with max-width */}
      <div className="flex-1 flex flex-col w-full max-w-5xl mx-auto">
        {/* Client Header */}
        <div className="px-8 py-6 border-b border-gray-200 dark:border-[#333]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center text-white text-lg font-semibold"
                style={{ backgroundColor: client?.color || '#6366f1' }}
              >
                {client?.name?.charAt(0)?.toUpperCase() || 'C'}
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-white" style={{ fontFamily: 'var(--chat-font-family)' }}>
                  {client?.name}
                </h1>
                {client?.description && (
                  <p className="text-sm text-gray-500 dark:text-gray-400" style={{ fontFamily: 'var(--chat-font-family)' }}>
                    {client.description}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Search & Actions Bar */}
        <div className="px-8 py-4 border-b border-gray-200 dark:border-[#333] flex items-center gap-4">
        <div className="flex-1 relative">
          <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search files..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-gray-100 dark:bg-[#222] border-0 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            style={{ fontFamily: 'var(--chat-font-family)' }}
          />
        </div>
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm disabled:opacity-50"
          style={{ fontFamily: 'var(--chat-font-family)' }}
        >
          {uploading ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <ArrowUpTrayIcon className="w-4 h-4" />
              Upload
            </>
          )}
        </button>
      </div>

      {/* Files Area */}
      <div className="flex-1 overflow-y-auto p-8">
        {filteredFiles.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredFiles.map((file) => {
              const FileIcon = getFileIcon(file.mimeType);
              const isImage = file.mimeType?.startsWith('image/');
              const isVideo = file.mimeType?.startsWith('video/');
              const isPdf = file.mimeType?.includes('pdf');
              const fileExtension = file.name?.split('.').pop()?.toUpperCase() || 'FILE';
              
              return (
                <div
                  key={file._id}
                  className="group relative bg-gray-50 dark:bg-[#1a1a1a] rounded-xl overflow-hidden hover:shadow-lg transition-shadow border border-gray-200 dark:border-[#333]"
                >
                  {/* Document Preview */}
                  <div className="aspect-[4/3] w-full bg-white dark:bg-[#1a1a1a] overflow-hidden relative">
                    {/* Image Preview */}
                    {isImage && file.url && (
                      <img 
                        src={file.url} 
                        alt={file.name}
                        className="w-full h-full object-contain bg-gray-100 dark:bg-[#222]"
                      />
                    )}
                    
                    {/* PDF Preview - show icon fallback (iframes don't work well with signed URLs) */}
                    {isPdf && (
                      <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100 dark:bg-[#222]">
                        <div className="w-16 h-20 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center shadow-lg mb-2">
                          <DocumentTextIcon className="w-8 h-8 text-white" />
                        </div>
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                          PDF
                        </span>
                      </div>
                    )}
                    
                    {/* Video Preview */}
                    {isVideo && file.url && (
                      <video 
                        src={file.url}
                        className="w-full h-full object-contain bg-black"
                        muted
                        preload="metadata"
                      />
                    )}
                    
                    {/* Fallback for other file types */}
                    {!isImage && !isPdf && !isVideo && (
                      <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100 dark:bg-[#222]">
                        <div className="w-16 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-lg mb-2">
                          <FileIcon className="w-8 h-8 text-white" />
                        </div>
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                          {fileExtension}
                        </span>
                      </div>
                    )}
                    
                    {/* Click overlay to open file */}
                    {file.url && (
                      <a
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute inset-0 z-10"
                        title="Open file"
                      />
                    )}
                  </div>

                  {/* File Info */}
                  <div className="p-3 border-t border-gray-200 dark:border-[#333]">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-0.5" style={{ fontFamily: 'var(--chat-font-family)' }} title={file.name}>
                      {truncateMiddle(file.name)}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400" style={{ fontFamily: 'var(--chat-font-family)' }}>
                      {formatFileSize(file.size)}{file.pageCount ? ` • ${file.pageCount} page${file.pageCount > 1 ? 's' : ''}` : ''} • {formatDate(file.createdAt)}
                    </p>
                  </div>

                  {/* Actions (show on hover) */}
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-20">
                    {file.url && (
                      <a
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 bg-white dark:bg-[#333] rounded-lg shadow hover:bg-gray-100 dark:hover:bg-[#444] transition-colors"
                        title="Open"
                      >
                        <ArrowDownTrayIcon className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                      </a>
                    )}
                    <button
                      onClick={() => handleDeleteFile(file._id)}
                      className="p-1.5 bg-white dark:bg-[#333] rounded-lg shadow hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                      title="Delete"
                    >
                      <TrashIcon className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* Empty State */
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-20 h-20 mb-6 bg-gray-100 dark:bg-[#222] rounded-2xl flex items-center justify-center">
              <FolderIcon className="w-10 h-10 text-gray-400 dark:text-gray-500" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2" style={{ fontFamily: 'var(--chat-font-family)' }}>
              {searchQuery ? 'No files found' : 'No files yet'}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-sm" style={{ fontFamily: 'var(--chat-font-family)' }}>
              {searchQuery 
                ? 'Try a different search term.'
                : 'Upload documents, contracts, notes, and other files related to this client.'
              }
            </p>
            {!searchQuery && (
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-[#222] text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-[#333] transition-colors text-sm disabled:opacity-50"
                style={{ fontFamily: 'var(--chat-font-family)' }}
              >
                <ArrowUpTrayIcon className="w-4 h-4" />
                Upload your first file
              </button>
            )}
          </div>
        )}
      </div>
      </div>{/* End max-width wrapper */}
    </div>
  );
};

export default ClientView;
